import { get, post, patch, deleted } from 'api/request'

export const API_ISSUE = "/project/service/issue"
export const API_ACTIVITY = "/contact/service/activity"
export const API_CONVERSATION = "/conversation/service/conversation"
export const API_PROJECT = "/project/service/project"
export const API_COMMENT = "/project/service/comment"
export const API_ACTION_LOG = "/project/service/action-log"

const getAPI = (mode) => {
  switch (mode) {
    case 'edit':
      return API_ISSUE;
    case 'all':
      return API_ISSUE;
    default:
      return API_PROJECT;
  }
}

export const renameDocument = (issue, key, attribute_id, file_name, data) => {
  let urlParams = {
    issue,
    key,
    attribute_id,
    file_name
  }
  return post(`${API_ISSUE}/${issue}/upload_issue_field`, data, { urlParams })
}

export const duplicateDocument = (issue, key, file_name, data) => {
  let urlParams = {
    issue,
    key,
    file_name
  }
  return post(`${API_ISSUE}/${issue}/upload_issue_field`, data, { urlParams })
}

export const createNewDocument = (issue, key, file_name, data) => {
  let urlParams = {
    issue,
    key,
    file_name
  }
  return post(`${API_ISSUE}/${issue}/upload_issue_field`, data, { urlParams })
}

export const getDocument = (issue, key, attribute_id) => {
  let urlParams = { issue, key }
  if (attribute_id) {
    urlParams.attribute_id = attribute_id
  }
  return get(`${API_ISSUE}/${issue}/download_issue_field`, { urlParams })
}

export const getIssueList = (offset = 0, size = 20, projectKey, filters = {}, order, issue_view) => {
  let params = { offset, size, issue_view }
  if (projectKey?.length) {
    if (projectKey.indexOf(',') > -1) {
      params.projects = projectKey
    } else {
      params.project = projectKey
    }
  }
  Object.keys(filters).forEach(k => {
    params[k] = filters[k]
  })
  if (order)
    params["order"] = order
  return get(API_ISSUE, { urlParams: params })
}
// One row per document of the media field `field`, over all issues of the project (the `document` issue view). Same `{ total, offset,
// size, has_more, records }` paging as the issue list; `filters` and `order` are the issue list's.
export const getDocumentList = (offset = 0, size = 20, projectKey, field, filters = {}, order, issue_view) => {
  const params = { field, issue_view, offset, size }
  Object.keys(filters).forEach(k => {
    params[k] = filters[k]
  })
  if (order)
    params["order"] = order
  return get(`${API_PROJECT}/${projectKey}/field_values/`, { urlParams: params })
}
export const getIssueDetail = (key) => get(`${API_ISSUE}/${key}`)
export const getIssueAssignees = (key) => get(`${API_ISSUE}/${key}/assignees`)
export const runIssueAction = (key, action, data = {}) => post(
  `${API_ISSUE}/${key}/run_action`,
  { action, ...data }
)
export const createIssue = (projectKey, data = {}) => post(
  `${API_ISSUE}`,
  { project: projectKey, ...data }
)
// Issue follow-up (v1, `issue/{key}/followup`) — the issue counterpart of the lead follow-up (leads/api/lead.js): a reason plus
// a list of attempts (sequences), read back from the issue's `followup_data` / `followup_since`.
//   set (PATCH followup, body { reason, sequences }) · delete (DELETE followup) · complete the whole follow-up (POST
//   followup/mark_complete) · complete the current attempt (POST followup/mark_sequence_done)
// Mirrors the lead endpoints' request shape — the v1 docs aren't published, so confirm the verbs / bodies with the backend.
export const setIssueFollowup = (key, data = {}) => patch(`${API_ISSUE}/${key}/followup`, data)
export const deleteIssueFollowup = (key) => deleted(`${API_ISSUE}/${key}/followup`)
export const completeIssueFollowup = (key) => post(`${API_ISSUE}/${key}/followup/mark_complete`, {})
export const markIssueFollowupSequenceDone = (key) => post(`${API_ISSUE}/${key}/followup/mark_sequence_done`, {})
export const updateIssue = (key, data = {}) => patch(
  `${API_ISSUE}/${key}`,
  data
)
export const deleteIssue = (key) => deleted(
  `${API_ISSUE}/${key}`
)
export const getIssueActivityList = (key, offset = 0, size = 20, filters = {}, extraTargetObjects = []) => {
  const targetObjects = [`project/Issue:${key}`, ...extraTargetObjects]
  let urlParams = { offset, size, target_objects: targetObjects.join(',') }
  Object.keys(filters).forEach(k => {
    urlParams[k] = filters[k]
  })
  return get(API_ACTIVITY, { urlParams })
}
export const getActivityDetail = (key) => get(`${API_ACTIVITY}/${key}`)
export const getConversationDetail = (key) => get(`${API_CONVERSATION}/${key}`)
export const getIssueFormFields = (key, mode, forceUseProjectMode = false) => {
  let urlParams = {
    fields: 'options'
  }

  const apiMode = forceUseProjectMode ? API_PROJECT : getAPI(mode)
  return get(`${apiMode}/${key}/${mode}_fields`, { urlParams })
} 
export const uploadMedia = (media, onUploadProgress = null, isFromComment = false) => {
  let formData = new FormData()
  formData.append('media', media)

  return post(`${isFromComment ? API_COMMENT : API_ISSUE}/upload_media`, formData, { timeout: 600000, onUploadProgress })
}
// extraTargetObjects: additional "type:uid" strings (e.g. lead/Lead:<uid> for a linked
// lead). When present, target_objects (this issue + the extras) is sent alongside the
// plain `issue` key so a linked lead's comments can fold into this feed — same
// target_objects convention getIssueActivityList uses. `issue` is kept regardless so a
// backend that doesn't honour target_objects on this endpoint still returns the issue's
// own comments unchanged.
export const getIssueCommentList = (key, offset = 0, size = 8, filters = {}, extraTargetObjects = []) => {
  let urlParams = { issue: key, offset, size }
  if (extraTargetObjects.length) {
    urlParams.target_objects = [`project/Issue:${key}`, ...extraTargetObjects].join(',')
  }
  Object.keys(filters).forEach(k => {
    urlParams[k] = filters[k]
  })
  return get(API_COMMENT, { urlParams })
}
export const sendIssueComment = (data) => post(`${API_COMMENT}`, data)
export const getIssueActionLogs = (key, offset = 0, size = 8, filters = {}) => {
  let urlParams = { issue: key, offset, size }
  Object.keys(filters).forEach(k => {
    urlParams[k] = filters[k]
  })
  return get(API_ACTION_LOG, { urlParams })
}
export const getIssueActivityUpdates = (project, issue, offset = 0, size = 20, filters = {}) => {
  let urlParams = { offset, size, project, issue }
  Object.keys(filters).forEach(k => {
    urlParams[k] = filters[k]
  })
  return get(`${API_ISSUE}/updates/`, { urlParams })
}
export const getPrintUrl = (key) => get(`${API_ISSUE}/${key}/download_pdf`, { timeout: 45000 })
export const getActivityPrintUrl = (key) => get(`${API_ACTIVITY}/${key}/download_pdf`)
export const addNewOption = (key, fieldName, option) => post(`${API_PROJECT}/${key}/field_options/?field=${fieldName}`, option)

// get single contact to state
export const API_CONTACT = "/contact/service/contact"
export const getContactDetail = (key) => get(`${API_CONTACT}/${key}`)
